import {
  getFontCss,
  HoverableLink,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import BaseFlyout, {
  BaseFlyoutProps,
} from 'ext-ui/components/BaseFlyout/BaseFlyout'
import Button from 'ext-ui/components/Button/Button'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import RadioGroup from 'ext-ui/components/RadioGroup/RadioGroup'
import {
  DesignSystemChannel,
  DesignSystemExperimentalVariant,
  DesignSystemReleaseChannel,
  getDesignSystemChannel,
  getDesignSystemExperimentalVariant,
  getDesignSystemVersions,
  setDesignSystemChannel,
  setDesignSystemExperimentalVariant,
  subscribeToDesignSystemChannel,
  subscribeToDesignSystemExperimentalVariant,
} from 'ext-ui/design-system/DesignSystemChannel'
import {
  base_colorContentBorder,
  base_colorRaisedSurface,
  base_colorSurface,
  gradient_selectedGradientEnd,
  gradient_selectedGradientStart,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import { transition_fast } from 'ext-ui/style/transitionSpeeds'
import React, { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { useSelector } from 'react-redux'
import Box from 'src/components/Box/Box'
import SvgTrash from 'src/components/Svgs/SvgTrash'
import TextDropdown from 'src/components/TextDropdown/TextDropdown'
import { useSessionStatus } from 'src/context/SessionStatusProvider/useSessionStatus'
import { CookieNames, CookieValuePaths } from 'src/cookies'
import { ExtendLocales } from 'src/enums'
import { useCookieState, useFlyoutDispatch } from 'src/hooks'
import {
  RemoteConfigKeys,
  remoteConfigKeys,
} from 'src/models/remoteConfig.models'
import { connectedPush } from 'src/navigation/Lnk'
import {
  loadFeatureFlags,
  updateFeatureFlag,
} from 'src/redux/actions/session.actions'
import {
  selectUserIdFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import {
  selectLocalAuthBypassFromState,
  selectLocalUserIdFromState,
} from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

interface FeatureItemProps {
  remoteConfigKey: RemoteConfigKeys
  cookieName: CookieNames
  checked?: boolean
}

const FeatureItem: React.FC<FeatureItemProps> = (props): React.ReactElement => {
  const dispatch = useFlyoutDispatch()
  const featureFlags = useSelector(selectFeatureFlaggerFromState)

  const { updateCookieValue: setCookieValue } = useCookieState<
    boolean | undefined
  >(props.cookieName, props.remoteConfigKey, undefined)

  const selectedValue =
    typeof props.checked === 'boolean'
      ? props.checked
      : featureFlags[props.remoteConfigKey]

  // TODO: do we want to handle other types of feature flags in the flyout?
  if (typeof selectedValue !== 'boolean') return <></>
  return (
    <label
      css={css`
        display: flex;
        padding: 10px;
        background-color: ${styleConstants.colors.white};
        width: 100%;
        cursor: pointer;
      `}
      htmlFor={props.remoteConfigKey}
    >
      <div
        css={css`
          flex-grow: 1;
        `}
      >
        <span>{props.remoteConfigKey.split('_').join(' ')}</span>
      </div>
      <Checkbox
        aria-label={props.remoteConfigKey}
        id={props.remoteConfigKey}
        onChange={(event) => {
          setCookieValue(event.target.checked) // update cookie value
          dispatch(
            updateFeatureFlag(props.remoteConfigKey, event.target.checked),
          ) // update redux value
        }}
        checked={selectedValue}
        data-testid={`checkbox-${props.remoteConfigKey}`}
        data-is-checked={props.checked}
      />
    </label>
  )
}

const LocalUserSwitcher: React.FC = () => {
  const localAuthBypass = useSelector(selectLocalAuthBypassFromState)
  const currentLocalUserId = useSelector(selectLocalUserIdFromState)
  const currentUserId = useSelector(selectUserIdFromState)
  const currentUser = useSelector(selectUserListModelFromState)
  const [inputUserId, setInputUserId] = useState(currentLocalUserId || '')

  if (!localAuthBypass) return null

  const handleSwitch = (): void => {
    if (!inputUserId.trim()) return
    localStorage.setItem('LOCAL_USER_ID_OVERRIDE', inputUserId.trim())
    window.location.reload()
  }

  const handleReset = (): void => {
    localStorage.removeItem('LOCAL_USER_ID_OVERRIDE')
    window.location.reload()
  }

  return (
    <Box
      css={css`
        padding: 20px;
        border-bottom: 2px solid ${styleConstants.colors.gray_light};
      `}
    >
      <div
        css={css`
          ${getFontCss(
            20,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )}
          margin-bottom: 8px;
        `}
      >
        {'Local User Switcher'}
      </div>
      <div
        css={css`
          font-size: 14px;
          color: ${styleConstants.colors.gray_medium};
          margin-bottom: 12px;
        `}
      >
        {currentUser
          ? `Current: ${currentUser.firstName} ${currentUser.lastName} (${currentUser.email})`
          : `Current: ${currentUserId || 'none'}`}
      </div>
      <div
        css={css`
          display: flex;
          gap: 8px;
          align-items: center;
        `}
      >
        <input
          type="text"
          value={inputUserId}
          onChange={(e): void => setInputUserId(e.target.value)}
          onKeyDown={(e): void => {
            if (e.key === 'Enter') handleSwitch()
          }}
          placeholder="u_userId"
          css={css`
            flex: 1;
            padding: 8px 12px;
            border: 1px solid ${styleConstants.colors.gray_medium};
            border-radius: 4px;
            font-size: 14px;
            font-family: monospace;
          `}
        />
        <Button type={'button'} variant={'primary'} onClick={handleSwitch}>
          {'Switch'}
        </Button>
        {typeof window !== 'undefined' &&
          localStorage.getItem('LOCAL_USER_ID_OVERRIDE') && (
            <Button type={'button'} variant={'severe'} onClick={handleReset}>
              {'Reset'}
            </Button>
          )}
      </div>
    </Box>
  )
}

const DesignSystemChannelSwitcher: React.FC = () => {
  const versions = getDesignSystemVersions()
  const [selectedChannel, setSelectedChannel] = useState<DesignSystemChannel>(
    getDesignSystemChannel,
  )
  const [selectedVariant, setSelectedVariant] =
    useState<DesignSystemExperimentalVariant>(
      getDesignSystemExperimentalVariant,
    )

  useEffect(() => subscribeToDesignSystemChannel(setSelectedChannel), [])
  useEffect(
    () => subscribeToDesignSystemExperimentalVariant(setSelectedVariant),
    [],
  )

  const setChannel = (channel: DesignSystemChannel): void => {
    setDesignSystemChannel(channel)
    setSelectedChannel(channel)
  }

  const setExperimentalVariant = (
    variant: DesignSystemExperimentalVariant,
  ): void => {
    setDesignSystemExperimentalVariant(variant)
    setSelectedVariant(variant)
  }

  const releaseChannelFor = (
    channel: DesignSystemChannel,
  ): DesignSystemReleaseChannel =>
    channel === 'experimental' ? 'latest' : channel

  const channelDescription = (channel: DesignSystemChannel): string => {
    const releaseChannel = releaseChannelFor(channel)
    const version = versions.channels[releaseChannel]
    const sourceSha = version.sourceSha
      ? version.sourceSha.slice(0, 12)
      : 'unversioned'
    const timestamp =
      releaseChannel === 'live'
        ? version.promotedAt || version.syncedAt
        : version.syncedAt
    const prefix =
      channel === 'experimental'
        ? `latest ${sourceSha} + experiments`
        : sourceSha

    return `${prefix}${timestamp ? ` - ${timestamp}` : ''}`
  }

  return (
    <Box
      css={css`
        padding: ${spacing(5)};
        padding-bottom: ${spacing(6)};
        margin-bottom: ${spacing(4)};
        border-bottom: 2px solid ${base_colorContentBorder};

        .FeatureFlaggerDesignSystemChannelGroup,
        .FeatureFlaggerDesignSystemVariantGroup {
          .RadioGroup--buttons {
            gap: ${spacing(3)};
          }
        }

        .FeatureFlaggerDesignSystemVariantGroup {
          margin-top: ${spacing(4)};
        }

        label.RadioButton.FeatureFlaggerDesignSystemChannelOption,
        label.RadioButton.FeatureFlaggerDesignSystemVariantOption {
          position: relative;
          overflow: hidden;
          border-color: ${base_colorContentBorder};
          background: ${base_colorRaisedSurface};
          transition:
            background-color ${transition_fast} ease,
            border-color ${transition_fast} ease,
            box-shadow ${transition_fast} ease,
            transform ${transition_fast} ease;

          .RadioButton--desc {
            color: ${text_colorTextSecondary};
          }

          &:has(> input:checked) {
            border-color: transparent;
            background:
              linear-gradient(${base_colorSurface}, ${base_colorSurface})
                padding-box,
              linear-gradient(
                  120deg,
                  ${gradient_selectedGradientStart},
                  ${gradient_selectedGradientEnd},
                  ${gradient_selectedGradientStart}
                )
                border-box;
            background-size:
              auto,
              220% 220%;
            box-shadow: 0 ${spacing(1)} ${spacing(4)} ${base_colorContentBorder};
            animation: featureFlaggerSelectedChannelBorder 4s ease infinite;
          }

          &:not(:has(> input:disabled)):hover {
            transform: translateY(-${spacing(0.5)});
          }
        }

        @keyframes featureFlaggerSelectedChannelBorder {
          0%,
          100% {
            background-position:
              0 0,
              0% 50%;
          }
          50% {
            background-position:
              0 0,
              100% 50%;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          label.RadioButton.FeatureFlaggerDesignSystemChannelOption,
          label.RadioButton.FeatureFlaggerDesignSystemVariantOption {
            transition: none;
            transform: none;

            &:has(> input:checked) {
              animation: none;
              background-position:
                0 0,
                50% 50%;
            }
          }
        }
      `}
    >
      <RadioGroup
        className="FeatureFlaggerDesignSystemChannelGroup"
        name="design-system-channel"
        label="Design System Channel"
        value={selectedChannel}
        onChange={(channel) => setChannel(channel)}
        options={(
          ['live', 'latest', 'experimental'] as DesignSystemChannel[]
        ).map((channel) => ({
          className: 'FeatureFlaggerDesignSystemChannelOption',
          value: channel,
          title:
            channel.charAt(0).toUpperCase() + channel.slice(1).toLowerCase(),
          description: channelDescription(channel),
          testId: `radio-design-system-channel-${channel}`,
        }))}
      />
      {selectedChannel === 'experimental' && (
        <RadioGroup
          className="FeatureFlaggerDesignSystemVariantGroup"
          name="design-system-experimental-variant"
          label="Experimental Variant"
          value={selectedVariant}
          onChange={(variant) => setExperimentalVariant(variant)}
          options={[
            {
              className: 'FeatureFlaggerDesignSystemVariantOption',
              value: 'compact',
              title: 'Compact',
              description: 'SaasNav compact visual treatment',
              testId: 'radio-design-system-experimental-variant-compact',
            },
            {
              className: 'FeatureFlaggerDesignSystemVariantOption',
              value: 'morph',
              title: 'Morph',
              description: 'SaasNav morph-to-expand rail',
              testId: 'radio-design-system-experimental-variant-morph',
            },
          ]}
        />
      )}
    </Box>
  )
}

type FeatureFlaggerProps = BaseFlyoutProps & {}

const FeatureFlagger: React.FC<FeatureFlaggerProps> = ({ isOpen, onClose }) => {
  const { isAuthenticated } = useSessionStatus()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const featureFlags = useSelector(selectFeatureFlaggerFromState)

  const dispatch = useFlyoutDispatch()

  const [appCookies, setAppCookies] = useCookies([CookieNames.App])
  const {
    cookieValue: unauthedLocaleCookie,
    updateCookieValue: setUnauthedLocaleCookie,
  } = useCookieState(
    CookieNames.Unauthed,
    CookieValuePaths.unauthedLocale,
    ExtendLocales.enUS,
  )

  const [_, __, removeFeatureFlaggerCookie] = useCookies([
    CookieNames.FeatureFlagger,
  ])

  return (
    <BaseFlyout
      isOpen={isOpen}
      onClose={onClose}
      title="Feature Flagger"
      subtitle="Toggle features on or off!"
      css={css`
        .BaseFlyout-Overlay {
          /* Extra high to always be on top! Also get over the dev tools button */
          z-index: 999;
        }
      `}
      aria-label={'Feature Flagger'}
    >
      <LocalUserSwitcher />
      <DesignSystemChannelSwitcher />
      <Box
        css={css`
          padding: 20px;
        `}
      >
        {remoteConfigKeys.map((key) => {
          const flagValue = featureFlags[key]
          const checked = typeof flagValue === 'boolean' ? flagValue : undefined
          return (
            <FeatureItem
              key={key}
              remoteConfigKey={key}
              cookieName={CookieNames.FeatureFlagger}
              checked={checked}
            />
          )
        })}
        <Button
          css={css`
            margin-top: 20px;
          `}
          type={'button'}
          variant={'severe'}
          onClick={(): void => {
            removeFeatureFlaggerCookie(CookieNames.FeatureFlagger)
            dispatch(loadFeatureFlags({}))
            window.location.reload()
          }}
        >
          {'Reset to Firebase Config'}
        </Button>
      </Box>
      {!isAuthenticated && (
        <Box
          css={css`
            margin-top: 20px;
            padding: 20px;
          `}
        >
          <TextDropdown
            fieldLabel={'Locale (page will reload after change)'}
            chooseOption={(locale: string): void => {
              setUnauthedLocaleCookie(locale as ExtendLocales)
              dispatch(connectedPush(calculatedBackgroundLocation))
              window.location.reload()
            }}
            selectedValues={new Set([unauthedLocaleCookie])}
            optionGroups={[
              {
                options: Object.values(ExtendLocales).map((locale) => ({
                  value: locale,
                })),
              },
            ]}
          />
        </Box>
      )}
      <Box
        css={css`
          margin-top: 20px;
          padding: 20px;
        `}
      >
        <div
          css={css`
            ${getFontCss(
              20,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )}
          `}
        >
          {'App Cookies'}
        </div>
        <div
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-top: 20px;
          `}
        >
          {appCookies?.app && Object.entries(appCookies.app).length > 0 ? (
            Object.entries(appCookies.app).map(([key, value]) => (
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  width: 100%;
                `}
                key={key}
              >
                <div>{key}</div>
                <div
                  css={css`
                    display: flex;
                    gap: 8px;
                    align-items: center;
                  `}
                >
                  <code>{`${value}`}</code>
                  <HoverableLink
                    ariaLabelSource={' '}
                    onClick={(): void => {
                      setAppCookies(
                        CookieNames.App,
                        {
                          ...(appCookies.app || {}),
                          [key]: undefined,
                        },
                        {
                          path: '/',
                          expires: new Date('January 1, 3000 00:00:00'),
                        },
                      )
                    }}
                  >
                    <SvgTrash />
                  </HoverableLink>
                </div>
              </div>
            ))
          ) : (
            <i>{'No cookies set'}</i>
          )}
        </div>
      </Box>
    </BaseFlyout>
  )
}

export default FeatureFlagger
